/**
 * IncidentTable Component - Phase 13
 * 
 * Table view for incidents with sorting and actions.
 */

import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  User,
  MapPin,
  Monitor,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Eye,
  MessageSquare,
  Flag,
  RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { getDomainDisplayLabel } from '@/lib/offlineAudit';

// Severity config
const SEVERITY_CONFIG = {
  critical: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' },
  error: { icon: XCircle, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30' },
  warning: { icon: AlertCircle, color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
  info: { icon: CheckCircle, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' }
};

// Status config
const STATUS_CONFIG = {
  observed: { label: 'Observed', color: 'bg-gray-100 text-gray-700' },
  queued: { label: 'Queued', color: 'bg-blue-100 text-blue-700' },
  retrying: { label: 'Retrying', color: 'bg-blue-100 text-blue-700' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
  conflict: { label: 'Conflict', color: 'bg-purple-100 text-purple-700' },
  manual_review: { label: 'Review', color: 'bg-amber-100 text-amber-700' },
  dismissed: { label: 'Dismissed', color: 'bg-gray-100 text-gray-700' }
};

// Format time ago
const formatTimeAgo = (ts) => {
  if (!ts) return 'Unknown';
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/**
 * IncidentTable Component
 */
export function IncidentTable({
  incidents,
  onViewDetails,
  onRetry,
  onMarkReviewed,
  onMarkNeedsFollowup,
  selectedIds = [],
  onSelectionChange,
  loading
}) {
  const [sortColumn, setSortColumn] = useState('occurredAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort indicator
  const SortIndicator = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-3 w-3 inline ml-1" /> : 
      <ChevronDown className="h-3 w-3 inline ml-1" />;
  };

  // Handle row selection
  const handleSelectAll = (checked) => {
    if (onSelectionChange) {
      onSelectionChange(checked ? incidents.map(i => i.incidentId) : []);
    }
  };

  const handleSelectRow = (incidentId, checked) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedIds, incidentId]);
      } else {
        onSelectionChange(selectedIds.filter(id => id !== incidentId));
      }
    }
  };

  const allSelected = incidents.length > 0 && selectedIds.length === incidents.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < incidents.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Clock className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (incidents.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="incident-table-empty">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p>No incidents found</p>
        <p className="text-sm">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="incident-table">
      <Table>
        <TableHeader>
          <TableRow>
            {onSelectionChange && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected)}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
            )}
            <TableHead 
              className="cursor-pointer select-none"
              onClick={() => handleSort('occurredAt')}
            >
              Time <SortIndicator column="occurredAt" />
            </TableHead>
            <TableHead 
              className="cursor-pointer select-none"
              onClick={() => handleSort('severity')}
            >
              Severity <SortIndicator column="severity" />
            </TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Domain</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Actor</TableHead>
            <TableHead className="w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {incidents.map((incident) => {
            const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.info;
            const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.observed;
            const SeverityIcon = severityConfig.icon;
            const isSelected = selectedIds.includes(incident.incidentId);

            return (
              <TableRow 
                key={incident.incidentId}
                className={cn(
                  "cursor-pointer hover:bg-muted/50",
                  isSelected && "bg-muted"
                )}
                onClick={() => onViewDetails?.(incident)}
                data-testid={`incident-row-${incident.incidentId}`}
              >
                {onSelectionChange && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) => handleSelectRow(incident.incidentId, checked)}
                    />
                  </TableCell>
                )}
                
                <TableCell className="whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{formatTimeAgo(incident.occurredAt)}</span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className={cn(
                    "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs",
                    severityConfig.bg
                  )}>
                    <SeverityIcon className={cn("h-3 w-3", severityConfig.color)} />
                    <span className="capitalize">{incident.severity}</span>
                  </div>
                </TableCell>
                
                <TableCell>
                  <Badge className={statusConfig.color}>
                    {statusConfig.label}
                  </Badge>
                </TableCell>
                
                <TableCell>
                  <Badge variant="outline">
                    {getDomainDisplayLabel(incident.domain)}
                  </Badge>
                </TableCell>
                
                <TableCell className="max-w-[200px]">
                  <div className="truncate font-medium" title={incident.title}>
                    {incident.title}
                  </div>
                  {incident.managerNote && (
                    <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
                      <MessageSquare className="h-3 w-3" />
                      <span>Has note</span>
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  {incident.locationId && (
                    <div className="flex items-center gap-1 text-sm">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <span className="truncate max-w-[80px]">{incident.locationId}</span>
                    </div>
                  )}
                  {incident.terminalId && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Monitor className="h-3 w-3" />
                      <span>{incident.terminalId}</span>
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  {incident.actor?.name ? (
                    <div className="flex items-center gap-1 text-sm">
                      <User className="h-3 w-3 text-muted-foreground" />
                      <span className="truncate max-w-[80px]">{incident.actor.name}</span>
                    </div>
                  ) : incident.actor?.userId ? (
                    <span className="text-xs font-mono text-muted-foreground">
                      {incident.actor.userId.slice(0, 8)}...
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">System</span>
                  )}
                </TableCell>
                
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewDetails?.(incident)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      {incident.status === 'failed' && onRetry && (
                        <DropdownMenuItem onClick={() => onRetry(incident)}>
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Retry
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => onMarkReviewed?.(incident)}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark Reviewed
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onMarkNeedsFollowup?.(incident)}>
                        <Flag className="h-4 w-4 mr-2" />
                        Needs Follow-up
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default IncidentTable;
